import type { BaseSchemas } from "api-types";

type NavItemRouteId = BaseSchemas.NavItemRouteId;
export const CHECKOUT_NAV_ITEM_ID = "checkout";
type CustomerNavItemId = NavItemRouteId | typeof CHECKOUT_NAV_ITEM_ID;

/**
 * Where a top-level nav item renders:
 * - `main`     — primary side-nav group
 * - `products` — bottom "Products" side-nav group
 * - `company`  — company profile menu (rendered outside the side-nav groups).
 *                Only renders when a company name is present; this is safe
 *                because nav items are only returned for a real company, whose
 *                name is required. See the invariant in use-navigation-items.
 * - `user`     — user menu (surfaced separately; excluded from the side-nav)
 * - `nested`   — only ever a sub-item of another item; never a top-level entry
 */
export type NavSection = "main" | "products" | "company" | "user" | "nested";

export interface NavPlacement {
  section: NavSection;
  /** Render order within the section (lower first). */
  order: number;
}

/**
 * Single source of truth for the customer side-nav order and grouping.
 *
 * The server decides *which* nav items exist (and their visibility); this file
 * owns *order* (the index within a section) and *grouping* (the section). The
 * order the server happens to return is intentionally ignored.
 *
 * Every `NavItemRouteId` must appear in exactly one section. The exhaustiveness
 * check below is a compile error if a new id is added to the API without being
 * given a home here.
 */
const NAV_LAYOUT = {
  main: [
    "home page title",
    "apply for funding title", // "Capital" slot before the first advance
    "financing page title", // "Capital"
    "banking page title", // banking application (shown instead of the items below)
    "banking accounts page title",
    "banking cards page title",
    "banking transactions page title",
    "banking payments page title",
    CHECKOUT_NAV_ITEM_ID,
    "performance page title",
    "intelligence page title",
    "connections page title",
    "internal nav title", // staff-only, always last
  ],
  products: ["accounting page title"],
  company: ["documents page title"],
  user: ["customer referrals page title"],
  // Sub-items only — placed by their parent, never rendered as a top-level entry.
  nested: [
    "financing dashboard title",
    "funds request title",
    "advances page title",
    "statements page title",
    "banking recipients page title",
    "performance overview page title",
    "performance ltv page title",
    "performance channels page title",
    "performance products title",
    "performance googleads page title",
    "performance metaads page title",
    "performance paid channel summary page title",
    "internal ltv cac page title",
    "internal revenue page title",
    "internal tiktok spend page title",
  ],
} as const satisfies Record<NavSection, readonly CustomerNavItemId[]>;

// Compile-time exhaustiveness guard: if a NavItemRouteId is missing from
// NAV_LAYOUT, `UnplacedNavItemId` is no longer `never` and this assignment
// fails to type-check, naming the unplaced id(s).
type PlacedNavItemId = (typeof NAV_LAYOUT)[NavSection][number];
type UnplacedNavItemId = Exclude<NavItemRouteId, PlacedNavItemId>;
const _assertAllNavItemsPlaced: UnplacedNavItemId extends never ? true : UnplacedNavItemId = true;

const NAV_PLACEMENT_BY_ID: ReadonlyMap<string, NavPlacement> = new Map(
  (Object.entries(NAV_LAYOUT) as Array<[NavSection, readonly CustomerNavItemId[]]>).flatMap(([section, ids]) =>
    ids.map((id, order): [string, NavPlacement] => [id, { section, order }])
  )
);

/** Looks up where a nav item renders. Unknown ids return `undefined`. */
export const getNavPlacement = (id: string): NavPlacement | undefined => NAV_PLACEMENT_BY_ID.get(id);

/**
 * Nav items whose top-level entry is itself a navigable link, in addition to
 * grouping its sub-items — e.g. "Performance" links to its overview page while
 * still expanding its children.
 */
const NAVIGABLE_NAV_IDS = [
  "performance page title",
  "banking accounts page title",
  "banking payments page title",
] as const satisfies readonly NavItemRouteId[];

const NAVIGABLE_NAV_ID_SET: ReadonlySet<string> = new Set(NAVIGABLE_NAV_IDS);

/** Whether a nav item's top-level entry should also be a navigable link. */
export const isNavigableNavItem = (id: string): boolean => NAVIGABLE_NAV_ID_SET.has(id);

/**
 * Nav items whose list page has sub-pages (e.g. create/detail) that live
 * under the same path prefix but sit outside the item's own router subtree —
 * e.g. /cards/create and /accounts/<uuid> are siblings of /cards and
 * /accounts, not descendants. Registering the item's own path as one of its
 * alternative paths makes getIsRouteSelected's prefix-match branch keep the
 * nav item active on those sub-pages.
 */
const PATH_PREFIX_MATCHED_NAV_IDS = [
  "banking accounts page title",
  "banking cards page title",
] as const satisfies readonly NavItemRouteId[];

const PATH_PREFIX_MATCHED_NAV_ID_SET: ReadonlySet<string> = new Set(PATH_PREFIX_MATCHED_NAV_IDS);

/** Whether a nav item's own path should be registered as an alternative path for prefix matching. */
export const needsPathPrefixMatching = (id: string): boolean => PATH_PREFIX_MATCHED_NAV_ID_SET.has(id);
